import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Activity,
  GitMerge,
  Loader2,
  Sparkles,
  Table,
} from 'lucide-react';

import { ErrorState } from '@/components/ui/ErrorState';
import { Spinner } from '@/components/ui/Spinner';
import { useDataset, useDatasetPreview } from '@/features/connectors/hooks';
import { useResolveDataset } from '@/features/mdm/hooks';
import { useProfileDataset } from '@/features/quality/hooks';
import { isApiError, type ApiError } from '@/lib/errors';
import { SearchableSelect } from '@/components/ui/SearchableSelect';

export function DatasetDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const dataset = useDataset(id);
  const hasStorage = Boolean(dataset.data?.storage_table);
  const preview = useDatasetPreview(id, hasStorage);
  const profile = useProfileDataset();
  const resolveDuplicates = useResolveDataset();
  const [selectedColumn, setSelectedColumn] = useState('');
  const [blockingKeys, setBlockingKeys] = useState<string[]>([]);

  const toggleBlockingKey = (column: string): void => {
    setBlockingKeys((current) =>
      current.includes(column) ? current.filter((c) => c !== column) : [...current, column],
    );
  };

  const isIdColumn = (column: string): boolean => column.trim().toLowerCase() === 'id';

  if (dataset.isPending) {
    return <Spinner label="Loading dataset…" />;
  }
  if (dataset.isError) {
    return <ErrorState error={dataset.error as ApiError} />;
  }

  const item = dataset.data;
  const columnNames = item.schema.map((column) => column.name);
  const baseFqn = item.om_fqn && item.om_fqn !== '' ? item.om_fqn : `dataset:${item.id}`;
  const activeColumn = selectedColumn !== '' ? selectedColumn : (columnNames[0] ?? '');

  return (
    <div className="space-y-7">
      <Link
        to={`/connectors/${item.connector_id}`}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-500 dark:text-ink-400 transition hover:text-brand-700"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to connector
      </Link>

      <div>
        <div className="eyebrow eyebrow-dot mb-2">
          <span>DATASET</span>
        </div>
        <h1 className="h-display">{item.name}</h1>
        <p className="mt-2 font-mono text-xs text-ink-500 dark:text-ink-400">
          {item.source_ref}
          {item.om_fqn && (
            <>
              {' '}
              · catalogued as{' '}
              <span className="rounded-md bg-brand-50 dark:bg-brand-900/30 px-1.5 py-0.5 font-semibold text-brand-700">
                {item.om_fqn}
              </span>
            </>
          )}
        </p>
      </div>

      {/* Data Quality */}
      <SectionShell
        icon={Activity}
        accent="sky"
        title="Data Quality"
        description="Profile this dataset to record metrics. Run it several times to build a trend, then detect anomalies."
      >
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={profile.isPending}
            onClick={() => profile.mutate(item.id)}
            className="btn-primary h-9 px-3 text-xs"
          >
            {profile.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Profiling…
              </>
            ) : (
              <>
                <Sparkles className="h-3 w-3" />
                Profile now
              </>
            )}
          </button>
          {profile.isSuccess && (
            <span className="font-mono text-xs text-brand-700">
              Recorded {profile.data.recorded} metric point(s).
            </span>
          )}
          {profile.isError && (
            <span className="font-mono text-xs text-accent-crimson">
              {isApiError(profile.error) ? profile.error.message : 'Profiling failed.'}
            </span>
          )}
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-ink-100 dark:border-white/5 pt-4">
          <Link
            to={`/quality?asset=${encodeURIComponent(baseFqn)}&metric=row_count`}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-brand-200 dark:border-brand-700/40 bg-white dark:bg-ink-900 px-3 text-xs font-semibold text-brand-700 shadow-soft transition hover:-translate-y-px hover:bg-brand-50"
          >
            Detect row_count
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>

          {columnNames.length > 0 && (
            <div className="flex items-end gap-2 text-xs text-ink-500 dark:text-ink-400">
              <span className="pb-1.5">Per-column null_rate:</span>
              <div className="w-64">
                <SearchableSelect
                  value={activeColumn}
                  onChange={(val) => setSelectedColumn(val)}
                  options={columnNames.map((name) => ({ label: name, value: name }))}
                />
              </div>
              <Link
                to={`/quality?asset=${encodeURIComponent(`${baseFqn}.${activeColumn}`)}&metric=null_rate`}
                className="pb-1.5 font-semibold text-brand-700 hover:text-brand-800"
              >
                Detect →
              </Link>
            </div>
          )}
        </div>
      </SectionShell>

      {/* Master Data */}
      <SectionShell
        icon={GitMerge}
        accent="violet"
        title="Master Data"
        description="Find duplicate records in this dataset and resolve them to golden records."
      >
        {columnNames.length > 0 && (
          <div className="mb-4 rounded-xl border border-ink-200/70 dark:border-white/10 bg-ink-50/50 dark:bg-white/[0.03] p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <p className="text-xs font-medium text-ink-700 dark:text-ink-200">
                Must match exactly{' '}
                <span className="font-normal text-ink-500 dark:text-ink-400">
                  (blocking keys — two rows are duplicates only if they share the same real value on
                  a selected column; a different value blocks the merge, and 0 / blank counts as “no
                  value” so it never confirms a match)
                </span>
              </p>
              <div className="flex shrink-0 items-center gap-1.5 text-xs">
                <button
                  type="button"
                  onClick={() => setBlockingKeys(columnNames.filter((name) => !isIdColumn(name)))}
                  className="font-semibold text-brand-700 hover:text-brand-800"
                >
                  Select all (except id)
                </button>
                <span className="text-ink-300">·</span>
                <button
                  type="button"
                  onClick={() => setBlockingKeys([])}
                  disabled={blockingKeys.length === 0}
                  className="font-semibold text-ink-500 dark:text-ink-400 hover:text-ink-700 disabled:opacity-40"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {columnNames.map((name) => {
                const active = blockingKeys.includes(name);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => toggleBlockingKey(name)}
                    className={
                      active
                        ? 'rounded-full bg-gradient-to-br from-brand-500 to-brand-700 px-2.5 py-1 font-mono text-[11px] font-semibold tracking-wide text-white shadow-glow'
                        : 'rounded-full bg-white dark:bg-ink-900 px-2.5 py-1 font-mono text-[11px] font-medium text-ink-600 dark:text-ink-300 ring-1 ring-ink-200 transition hover:-translate-y-px hover:ring-brand-300'
                    }
                  >
                    {name}
                  </button>
                );
              })}
            </div>
            {blockingKeys.length > 0 && (
              <p className="mt-3 text-xs text-ink-500 dark:text-ink-400">
                Blocking on:{' '}
                <span className="font-mono font-semibold text-ink-800 dark:text-ink-100">
                  {blockingKeys.join(', ')}
                </span>
                . Two records are duplicates only if these are identical.
              </p>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={resolveDuplicates.isPending}
            onClick={() =>
              resolveDuplicates.mutate({
                datasetId: item.id,
                input: blockingKeys.length > 0 ? { exact_match_attributes: blockingKeys } : {},
              })
            }
            className="btn-primary h-9 px-3 text-xs"
          >
            {resolveDuplicates.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Matching…
              </>
            ) : (
              <>
                <GitMerge className="h-3 w-3" />
                Find duplicates
              </>
            )}
          </button>
          {resolveDuplicates.isSuccess && (
            <span className="text-xs text-brand-700">
              Found {resolveDuplicates.data.length} match group(s).{' '}
              <Link to="/mdm" className="font-semibold hover:text-brand-800">
                Review in Master Data →
              </Link>
            </span>
          )}
          {resolveDuplicates.isError && (
            <span className="font-mono text-xs text-accent-crimson">
              {isApiError(resolveDuplicates.error)
                ? resolveDuplicates.error.message
                : 'Matching failed.'}
            </span>
          )}
        </div>
      </SectionShell>

      {/* Schema */}
      <SectionShell
        icon={Table}
        accent="brand"
        title="Schema"
        description={`${item.column_count} column(s)`}
      >
        <div className="overflow-hidden rounded-lg border border-ink-200/70 dark:border-white/10">
          <table className="w-full">
            <thead className="bg-ink-50/70 dark:bg-white/[0.03]">
              <tr className="font-mono text-[10px] font-semibold tracking-[0.14em] text-ink-500 dark:text-ink-400">
                <th className="px-4 py-2.5 text-left">COLUMN</th>
                <th className="px-4 py-2.5 text-left">TYPE</th>
                <th className="px-4 py-2.5 text-left">NULLABLE</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100 dark:divide-white/5">
              {item.schema.map((column) => (
                <tr key={column.name} className="transition hover:bg-brand-50/30">
                  <td className="px-4 py-2.5 font-mono text-[13px] font-medium text-ink-800 dark:text-ink-100">
                    {column.name}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-ink-500 dark:text-ink-400">{column.data_type}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">
                    {column.nullable === false ? (
                      <span className="text-ink-500 dark:text-ink-400">no</span>
                    ) : (
                      <span className="text-brand-700">yes</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionShell>

      {/* Preview */}
      <SectionShell
        icon={Table}
        accent="amber"
        title="Preview"
        description="First rows of the dataset."
      >
        {!hasStorage ? (
          <p className="rounded-lg border border-dashed border-ink-300 dark:border-white/15 bg-ink-50/40 dark:bg-white/[0.02] p-4 text-sm text-ink-500 dark:text-ink-400">
            Preview is available for uploaded file datasets. Database tables are catalogued in
            place.
          </p>
        ) : preview.isPending ? (
          <Spinner />
        ) : preview.isError ? (
          <ErrorState error={preview.error as ApiError} />
        ) : preview.data.length === 0 ? (
          <p className="text-sm text-ink-500 dark:text-ink-400">No rows.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-ink-200/70 dark:border-white/10">
            <table className="min-w-full">
              <thead className="bg-ink-50/70 dark:bg-white/[0.03]">
                <tr className="font-mono text-[10px] font-semibold tracking-[0.14em] text-ink-500 dark:text-ink-400">
                  {columnNames.map((name) => (
                    <th key={name} className="px-3 py-2.5 text-left">
                      {name.toUpperCase()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100 dark:divide-white/5">
                {preview.data.map((row, index) => (
                  <tr key={index} className="transition hover:bg-brand-50/30">
                    {columnNames.map((name) => (
                      <td key={name} className="px-3 py-2 font-mono text-xs text-ink-700 dark:text-ink-200">
                        {formatCell(row[name])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionShell>
    </div>
  );
}

function SectionShell({
  icon: Icon,
  accent,
  title,
  description,
  children,
}: {
  icon: typeof Table;
  accent: 'brand' | 'sky' | 'violet' | 'amber';
  title: string;
  description: string;
  children: React.ReactNode;
}): JSX.Element {
  const meta: Record<typeof accent, { bg: string; fg: string }> = {
    brand: { bg: 'bg-brand-50 dark:bg-brand-900/30', fg: 'text-brand-600' },
    sky: { bg: 'bg-accent-sky/10', fg: 'text-accent-sky' },
    violet: { bg: 'bg-accent-violet/10', fg: 'text-accent-violet' },
    amber: { bg: 'bg-accent-amber/10', fg: 'text-accent-amber' },
  };
  const m = meta[accent];
  return (
    <section className="rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 shadow-soft animate-slide-up">
      <header className="flex items-start gap-3 border-b border-ink-100 dark:border-white/5 px-6 py-4">
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${m.bg} ${m.fg}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <h3 className="h-section">{title}</h3>
          <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">{description}</p>
        </div>
      </header>
      <div className="p-6">{children}</div>
    </section>
  );
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}
